import { useEffect, useRef, type InputHTMLAttributes } from 'react';
import { Icon } from './Icon';
import './Checkbox.css';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'checked'> {
  checked: boolean;
  /** True `undefined`/partial selection state (e.g. "some but not all visible rows selected").
   * Visual + AT state only — `indeterminate` isn't settable via JSX, so it's applied imperatively. */
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  /** Hides the visible label text but keeps it for assistive tech — used for a bare row/tile
   * select checkbox whose row already states what it selects. */
  hideLabel?: boolean;
}

/** DESIGN.md §9.1 `Checkbox`. Used by FacetPanel's multi-select value lists and by the
 * library grid/list row-select affordance (AC-UI-9). */
export function Checkbox({ checked, indeterminate = false, onChange, label, hideLabel = false, disabled, className, ...rest }: CheckboxProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label className={['checkbox', disabled ? 'checkbox--disabled' : '', className].filter(Boolean).join(' ')}>
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.currentTarget.checked)}
        {...rest}
      />
      <span className="checkbox__box" aria-hidden="true">
        <Icon name="check" size={12} />
      </span>
      {/* MVP-264: see `Switch.tsx` — `hideLabel` removed the name instead of hiding it. 59 of the
          73 checkboxes on the library screen (every per-song select box) announced identically with
          no title. Visually hidden, not removed. */}
      <span className={hideLabel ? 'visually-hidden' : 'text-body'}>{label}</span>
    </label>
  );
}
